import { z } from "zod";
import {
  Axis,
  BarSeries,
  ChartGrid,
  ChartTooltip,
  angledTickLabelProps,
  Box,
  WidgetEmpty,
  WidgetSkeleton,
  XYChart,
  useCategoricalColors,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  truncateLabel,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { resolveStoredTitle } from "../shared/derived-titles";
import { useStageAge, type StageAgeRow } from "./use-stage-age";

/**
 * `stage-age` — a themed bar chart of how long projects dwell in each stage
 * (average or maximum days), on the `XYChart` scaffold. Answers the Add-card
 * "Total time in stage / Age" bar. Dwell is real time-in-stage from the server's
 * `issueStageDwell` aggregation over timestamped stage-transition activities.
 * Each bar wears its stage's OWN color (`useCategoricalColors`); pressing one
 * opens this card's Data drawer on that stage's projects (`dimension: "stage"`).
 */
export const stageAgeConfigSchema = z
  .looseObject({
    measure: z.enum(["avg", "max"]).default("avg"),
    sort: z.enum(["duration", "alpha"]).default("duration"),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  });

export type StageAgeConfig = z.infer<typeof stageAgeConfigSchema>;

/** Title mechanics: the per-measure derived titles plus the generic name are
 *  widget-generated and re-derive when the measure flips; renames stick. */
const AGE_MEASURE_TITLE: Record<StageAgeConfig["measure"], string> = {
  avg: "Average time in stage",
  max: "Max time in stage",
};
const GENERATED_AGE_TITLES = new Set(["Time in stage", ...Object.values(AGE_MEASURE_TITLE)]);

const xAccessor = (d: StageAgeRow) => d.label;
const yAccessor = (d: StageAgeRow) => d.days;

function StageAgeRender({ h, config }: WidgetRenderContext<StageAgeConfig>) {
  const { data, loading, error } = useStageAge(config.measure, config.sort, config.filters);
  const sliceColors = useCategoricalColors(data);
  // Click-through: a bar press opens this card's Data drawer on the stage's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="rows" />;
  }
  if (data.length === 0) {
    return <WidgetEmpty icon="clock" title="No projects in range" />;
  }

  const compact = h < 3;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;

  return (
    <Box direction="column" fill="flex" gap="xs" cursor={drill && instance ? "pointer" : undefined}>
      <XYChart<StageAgeRow>
        xScale={{ type: "band", paddingInner: 0.3, paddingOuter: 0.2 }}
        yScale={{ type: "linear", nice: true }}
        margin={{ top: 8, right: 8, bottom: showAxes ? 52 : 8, left: showAxes ? 36 : 8 }}
        ariaLabel="Project age by stage"
        onPointerUp={
          drill && instance
            ? ({ datum }) => {
                if (datum)
                  drill.openData(instance.instanceId, {
                    kind: "dimension",
                    dimension: "stage",
                    ids: [datum.id],
                    label: datum.label,
                  });
              }
            : undefined
        }
      >
        {showGrid ? <ChartGrid rows columns={false} numTicks={4} /> : null}
        {showAxes ? <Axis orientation="left" numTicks={4} tickFormat={(v) => `${v}d`} /> : null}
        {showAxes ? (
          <Axis
            orientation="bottom"
            tickFormat={(v) => truncateLabel(String(v))}
            tickLabelProps={angledTickLabelProps}
          />
        ) : null}
        <BarSeries
          dataKey="days"
          data={data}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          colorAccessor={(d) => sliceColors.scale(d.label)}
        />
        <ChartTooltip
          snapTooltipToDatumX
          showVerticalCrosshair
          renderTooltip={({ tooltipData }) => {
            const d = tooltipData?.nearestDatum?.datum as StageAgeRow | undefined;
            if (!d) return null;
            return (
              <WidgetChartTooltip
                label={d.label}
                details={`${d.days} ${d.days === 1 ? "day" : "days"} ${config.measure === "max" ? "oldest" : "avg"} · ${d.count} ${d.count === 1 ? "project" : "projects"}`}
              />
            );
          }}
        />
      </XYChart>
    </Box>
  );
}

const stageAgeSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "measure",
        kind: "select",
        label: "Measure",
        icon: "clock",
        options: [
          { value: "avg", label: "Average age" },
          { value: "max", label: "Oldest project" },
        ],
      },
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: [
          { value: "duration", label: "Longest" },
          { value: "alpha", label: "Alphabetical" },
        ],
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
      { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const stageAgeDef: WidgetModule<StageAgeConfig> = {
  type: "stage-age",
  title: "Time in stage",
  dynamicTitle: (config) =>
    resolveStoredTitle(
      config.title,
      (title) => GENERATED_AGE_TITLES.has(title),
      AGE_MEASURE_TITLE[config.measure],
    ),
  icon: "clock",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 8, defaultW: 5, defaultH: 4 },
  configSchema: stageAgeConfigSchema,
  settings: stageAgeSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "stage-age.stage",
      label: "Time in stage",
      description: "How long projects have been sitting in each pipeline stage, as bars.",
      section: "Charts",
      tags: ["projects", "stages", "ageHealth"] satisfies readonly WidgetTag[],
      // Label is in the generated family — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { measure: "avg" },
      defaultW: 5,
      defaultH: 4,
    },
  ],
  render: StageAgeRender,
};
